"use client";

import React, { useState } from "react";
import { cn } from "@/utilities/core/cn";

export interface StageTab {
    id: string;
    label: string;
}

export interface StageTabsProps {
    tabs: StageTab[];
    /** Renders the body for the active tab id. */
    renderBody: (activeId: string) => React.ReactNode;
    className?: string;
    /** Optional initial tab id; defaults to the first tab. */
    defaultActiveId?: string;
}

/**
 * Stage tab switcher — used to flip between standings of multiple stages
 * (e.g. championship / Europe / relegation playoff groups).
 *
 * If only one tab is supplied, the tab strip is hidden and the body
 * renders directly — keeps single-stage leagues visually unchanged.
 */
export const StageTabs: React.FC<StageTabsProps> = ({ tabs, renderBody, className, defaultActiveId }) => {
    const initialId = defaultActiveId ?? tabs[0]?.id;
    const [activeId, setActiveId] = useState<string | undefined>(initialId);

    if (tabs.length === 0) return null;
    if (tabs.length === 1) return <>{renderBody(tabs[0].id)}</>;

    return (
        <div className={cn("flex flex-col", className)}>
            <div
                role="tablist"
                className="flex flex-nowrap gap-1 px-2 py-2 bg-card overflow-x-auto overflow-y-hidden touch-pan-x overscroll-x-contain scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
                {tabs.map((t) => {
                    const isActive = t.id === activeId;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActiveId(t.id)}
                            className={cn(
                                "shrink-0 whitespace-nowrap px-3 py-1.5 text-xs font-bold uppercase tracking-wide",
                                isActive ? "tab-active" : "tab-inactive"
                            )}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>
            <div role="tabpanel">{activeId ? renderBody(activeId) : null}</div>
        </div>
    );
};
